import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import arraySupport from 'dayjs/plugin/arraySupport';

import { appActions, rtApiUsageActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { Col, Div, Dropdown, Icon, Row, Text, Switch as ToggleSwitch } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';
import { Card, Message, Switch, Button } from 'src/components';
import { LabeledCountPanel } from 'src/components/realtime_api_demo/panel';
import CONSTANTS from 'src/store/constants';
import { formatNumber, unifyApiStatus } from 'src/functions';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(arraySupport);

const menuItems = CONSTANTS.REAL_TIME_API.USAGE.FILTERS.map((filter) => (
  <Div k={filter}>{CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[filter]}</Div>
));

const today = dayjs.utc();

function aggregateByPredicate(array, predicate, target) {
  return array.map((label) => {
    const filtered = target.filter(predicate(label));
    const count = filtered.reduce((acc, cur) => acc + cur.count, 0);
    return count === 0 ? null : count;
  });
}

function RtApiUsage({
  app: {
    profile: { tenant },
    client,
  },
  history,
  match: {
    params: { configId },
  },
  rtApiUsage: {
    config,
    fetchConfigStatus,
    fetchConfigError,
    configList,
    fetchConfigListStatus,
    fetchConfigListError,
    entries,
    fetchEntriesStatus,
    fetchEntriesError,
    generateStatsStatus,
    masks,
    ytdCount,
    mtdCount,
    tdyCount,
  },
  enqueueNotification,
  fetchConfig,
  fetchConfigList,
  handleUnmount,
  fetchEntries,
  generateStats,
}) {
  const [unifiedConfigApiStatus, setUnifiedConfigApiStatus] = useState(null);
  const [stackedTheme, setStackedTheme] = useState('pinpoint1');
  const [rangeSelectionOpen, setRangeSelectionOpen] = useState(false);
  const [usageFilter, setUsageFilter] = useState(CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_7_DAYS);
  const [maskName, setMaskName] = useState('Total');

  const toggleMask = useCallback(() => {
    if (maskName === 'Matched') setMaskName('Total');
    else setMaskName('Matched');
  }, [maskName]);

  const chartData = useMemo(() => {
    const data = {
      title: `Usage Over ${CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[usageFilter]}`,
      series: [],
    };
    if (client && client?.billingDay) {
      let predicate = null;
      let maskPrefix = null;
      if (usageFilter === CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_MONTHS) {
        maskPrefix = '1y';
        data.labels = Array(12)
          .fill(0)
          .map((_, idx) => today.subtract(idx, 'M').format('MMM'))
          .reverse();
        const labelsYearMonth = Array(12)
          .fill(0)
          .map((_, idx) => today.subtract(idx, 'M').format('YYYY-MMM'))
          .reverse();
        predicate = (label) => (entry) => {
          const index = data.labels.indexOf(label);
          const entryYearMonth = dayjs.utc(entry.period, 'YYYY-MM-DD').format('YYYY-MMM');
          return entryYearMonth === labelsYearMonth[index];
        };
      } else if (usageFilter === CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES) {
        const billingCycleEnd = dayjs.utc([dayjs.utc().year(), dayjs.utc().month(), client.billingDay]);
        // const isNewBillingPeriod = today.isSameOrAfter(billingCycleEnd.add(1, 'day'));
        maskPrefix = '6c';
        data.labels = Array(6)
          .fill(0)
          .map((_, idx) => billingCycleEnd.subtract(idx, 'M').format('MMM DD YYYY'))
          .reverse();
        data.labels[5] = 'Cur';
        predicate = (label) => (entry, idx) => {
          const entryPeriod = dayjs.utc(entry.period, 'YYYY-MM-DD');
          // Handle current cycle entry
          if (entryPeriod.isBetween(billingCycleEnd.subtract(1, 'M'), billingCycleEnd, 'day', '[)') && label === 'Cur')
            return true;

          // Handle past cycle entry
          const labelPeriodEnd = dayjs.utc(
            `${label.split(',')[0]}-${client.billingDay}-${label.split(',')[1]}`,
            `MMM-D-YYYY`
          );
          const labelPeriodStart = labelPeriodEnd.subtract(1, 'M');
          return entryPeriod.isBetween(labelPeriodStart, labelPeriodEnd, 'day', '[)');
        };
      } else if (usageFilter === CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_30_DAYS) {
        maskPrefix = '30d';
        data.labels = Array(30)
          .fill(0)
          .map((_, idx) => today.subtract(idx, 'day').format('YYYY-MM-DD'))
          .reverse();
        predicate = (label) => (entry) => entry.period === label;
      } else {
        maskPrefix = '7d';
        data.labels = Array(7)
          .fill(0)
          .map((_, idx) => today.subtract(idx, 'day').format('YYYY-MM-DD'))
          .reverse();
        predicate = (label) => (entry) => entry.period === label;
      }
      let primaryFilter = entries.filter((_, idx) => masks[`${maskPrefix}${maskName}`][idx]);
      if (maskPrefix === '6c')
        primaryFilter = primaryFilter.concat(entries.filter((_, idx) => masks[`btd${maskName}`][idx]));
      let secondaryFilter =
        maskName === 'Matched' ? entries.filter((_, idx) => masks[`${maskPrefix}Unmatched`][idx]) : null;
      if (maskPrefix === '6c' && secondaryFilter)
        secondaryFilter = secondaryFilter.concat(entries.filter((_, idx) => masks.btdUnmatched[idx]));
      data.series.push({
        name: maskName,
        values: aggregateByPredicate(data.labels, predicate, primaryFilter),
      });
      if (secondaryFilter)
        data.series.push({
          name: 'Unmatched',
          values: aggregateByPredicate(data.labels, predicate, secondaryFilter),
        });
      if (maskPrefix === '6c') data.labels = data.labels.map((label) => (label !== 'Cur' ? label.slice(0, -4) : label));
    }
    return data;
  }, [usageFilter, entries, masks, maskName, client]);

  const chartOptions = useMemo(() => {
    const options = {
      grid: {
        top: '10%',
        bottom: 112,
      },
      xAxis: {
        name:
          usageFilter === CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES
            ? 'Billing cycle ending on'
            : 'Period',
        axisLabel: {
          show: true,
          interval: 'auto',
        },
      },
      yAxis: {
        name: 'Number of calls',
      },
      tooltip: {
        show: false,
      },
      title: {
        top: 0,
      },
      legend: {
        itemGap: 24,
      },
      label: {
        position: 'top',
      },
      useUTC: true,
    };
    if (chartData.series.length >= 1)
      options.series = [
        {
          itemStyle: {
            color: '#FFBA00',
          },
        },
      ];
    if (chartData.series.length === 2)
      options.series.push({
        itemStyle: {
          color: '#A0A0A0',
        },
      });
    return options;
  }, [chartData, usageFilter]);

  const toggleRangeSelectionOpen = useCallback(
    () => setRangeSelectionOpen(!rangeSelectionOpen),
    [setRangeSelectionOpen, rangeSelectionOpen]
  );
  const loadRtData = useCallback(() => {
    if (configId) fetchConfig(configId);
    else if (configList.length === 0)
      fetchConfigList({
        filters: [
          {
            name: 'recent_status',
            op: 'in',
            val: [CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_SUCCESS],
          },
          {
            name: 'score_type',
            op: 'not_in',
            val: ['static'],
          },
        ],
        order_by: [{ field: 'created_at', direction: 'asc' }],
      });
  }, [fetchConfig, configId, fetchConfigList, configList.length]);

  useEffect(() => {
    if (tenant) loadRtData();
  }, [tenant, loadRtData]);

  useEffect(() => {
    setUnifiedConfigApiStatus(unifyApiStatus(false, fetchConfigStatus, fetchConfigListStatus));
  }, [fetchConfigListStatus, fetchConfigStatus]);

  useEffect(() => {
    if (fetchConfigListStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(fetchConfigListError);
    }
    if (fetchConfigStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(fetchConfigError);
    }
    if (fetchEntriesStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(fetchEntriesError);
    }
  }, [
    fetchConfigListStatus,
    fetchConfigListError,
    fetchConfigStatus,
    fetchConfigError,
    fetchEntriesStatus,
    fetchEntriesError,
    unifiedConfigApiStatus,
    enqueueNotification,
  ]);

  useEffect(() => {
    if (unifiedConfigApiStatus === CONSTANTS.API.SUCCEEDED) {
      const query = {
        filters: [{ name: 'period', op: 'gte', val: today.subtract(366, 'd').format('YYYY-MM-DD') }],
        order_by: [{ field: 'period', direction: 'asc' }],
      };
      if (configId) {
        query.filters.push({ val: configId, op: 'eq', name: 'name' });
      } else {
        query.filters.push({
          val: [...configList, { id: 'scores' }].map((cs) => cs.id),
          op: 'in',
          name: 'name',
        });
      }
      fetchEntries(query);
    }
  }, [unifiedConfigApiStatus, configId, configList, fetchEntries]);

  useEffect(() => {
    if (fetchEntriesStatus === CONSTANTS.API.SUCCEEDED) {
      generateStats(false);
    }
  }, [generateStats, fetchEntriesStatus]);

  return (
    <Div d="flex" flexDir="column" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem' }}>
        <Col justify="left" align="center" gutterWidth="0" d="flex" size="12">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push('/rt');
              handleUnmount();
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
        </Col>
      </Row>
      <Row m={{ x: '1.5rem', t: '1.5rem' }} flexDir="row" flexGrow="1">
        <Col size="12" justify="left" gutterWidth="0" d="flex" alignSelf="stretch">
          <Card
            title={
              unifiedConfigApiStatus === CONSTANTS.API.SUCCEEDED ? (
                <Div d="flex">
                  <Text textWeight="600">{configId ? 'Usage:' : 'Total Usage'}</Text>
                  <Text>&ensp;{get(config, 'config_metadata.model.name')}</Text>
                </Div>
              ) : null
            }
            iconProps={{
              'data-debug': 'card-icon-debug',
              name: configId ? 'Usage' : 'TotalUsage',
              color: 'white',
              size: '3.125rem',
              viewBox: '0 0 18 16',
            }}
            titleProps={{
              'data-debug': 'card-title-debug',
              bg: 'marigold',
              h: '5.5rem',
              p: { l: '2.5rem', r: '2.625rem' },
              rounded: { tr: 'md', tl: 'md' },
            }}
            titleTextProps={{
              'data-debug': 'card-title-text-debug',
              textColor: 'white',
              textSize: 'title',
              lineHeight: 'title',
            }}
            cardProps={{
              'data-debug': 'card-debug',
              p: '0',
              textSize: 'paragraph',
              style: {
                lineHeight: '1.5rem',
              },
              rounded: 'md',
              textColor: 'shade66',
              d: 'flex',
              flexDir: 'column',
            }}
          >
            <Switch status={unifiedConfigApiStatus}>
              <Message bg="white" value={CONSTANTS.API.STARTED} />
              <Message type="error" bg="white" value={CONSTANTS.API.FAILED} />
              <Col value={CONSTANTS.API.SUCCEEDED} gutterWidth="0" size="12" flexGrow="1" d="flex" flexDir="column">
                <Row p={{ t: '3.375rem', x: '2.5rem' }} flexDir="row">
                  <Col gutterWidth="0" size="2" align="center" d="flex">
                    <Text textColor="shade66" textSize="input" textWeight="500">
                      Stats
                    </Text>
                  </Col>
                  <Col size="7" textColor="shade66" textSize="small" data-debug="col" d="flex" align="center">
                    <Text textColor="shade66" textSize="input" textWeight="500">
                      Show Usage for
                    </Text>
                    <Dropdown
                      alignSelf="baseline"
                      h="2rem"
                      textSize="1rem"
                      w="14rem"
                      m={{ l: '1rem' }}
                      isOpen={rangeSelectionOpen}
                      onClick={toggleRangeSelectionOpen}
                      defaultValue={menuItems[0]}
                      menu={menuItems}
                      onSelect={(index) => {
                        setUsageFilter(CONSTANTS.REAL_TIME_API.USAGE.FILTERS[index]);
                      }}
                      rounded={rangeSelectionOpen ? { tl: 'md', bl: '0', br: '0', tr: 'md' } : 'md'}
                      border={rangeSelectionOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid'}
                      style={{
                        transition: 'all 0.2s ease-in-out',
                      }}
                      menuItemOptions={{
                        p: { y: '.25rem', x: '.75rem' },
                        textWeight: '500',
                        bg: 'shadeF2',
                        hoverBg: 'white',
                        style: {
                          transition: 'all 0.2s ease-in-out',
                        },
                      }}
                      menuOptions={{
                        border: { b: '1px solid', x: '1px solid', t: '0' },
                        rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
                        pos: 'absolute',
                        shadow: 4,
                        minW: '14rem',
                        maxW: '14rem',
                        style: {
                          transition: 'all 0.2s ease-in-out',
                        },
                        maxH: '250px',
                        overflow: 'auto',
                        textColor: 'shade66',
                        m: { t: '-2px', l: '-1px' },
                      }}
                    />
                  </Col>
                  <Col
                    gutterWidth="0"
                    textAlign="right"
                    textColor="shade66"
                    textSize="small"
                    size="3"
                    justifySelf="flex-end"
                    align="center"
                  >
                    <Row w="100%" p="0" m="0" align="center" justify="flex-end">
                      <Text textColor="shade66" textSize="input" textWeight="500" m={{ r: '.5rem' }}>
                        Show Matches
                      </Text>
                      <Div d="inline-block" m="0" onClick={toggleMask}>
                        <ToggleSwitch
                          checked={maskName === 'Matched'}
                          activeColor="marigold"
                          inactiveColor="shadeA0"
                          activeShadow="0"
                          inactiveShadow="0"
                          p="0"
                          m="0"
                        />
                      </Div>
                    </Row>
                  </Col>
                </Row>
                <Row p={{ t: '.75rem', x: '2.5rem', b: '2rem' }} flexGrow="1">
                  <Col size="2" d="flex" flexDir="column" justify="flex-start" align="center" gutterWidth="0">
                    <Row m={{ b: '1rem' }} w="100%">
                      <LabeledCountPanel
                        status={generateStatsStatus}
                        value={ytdCount}
                        title="Year to Date"
                        panelProps={{
                          p: '1.5rem',
                          w: '100%',
                        }}
                        countProps={{
                          textWeight: '500',
                          textSize: 'display1',
                          textColor: 'black',
                        }}
                        titleProps={{
                          textWeight: '500',
                          textSize: 'body',
                          textColor: 'marigold',
                        }}
                      />
                    </Row>
                    <Row m={{ b: '1rem' }} w="100%">
                      <LabeledCountPanel
                        status={generateStatsStatus}
                        value={mtdCount}
                        title="Month to Date"
                        panelProps={{
                          p: '1.5rem',
                          w: '100%',
                        }}
                        countProps={{
                          textWeight: '500',
                          textColor: 'black',
                          textSize: 'display1',
                        }}
                        titleProps={{
                          textWeight: '500',
                          textSize: 'body',
                          textColor: 'marigold',
                        }}
                      />
                    </Row>
                    <Row w="100%">
                      <LabeledCountPanel
                        status={generateStatsStatus}
                        value={tdyCount}
                        title="Today"
                        panelProps={{
                          p: '1.5rem',
                          w: '100%',
                        }}
                        countProps={{
                          textWeight: '500',
                          textColor: 'black',
                          textSize: 'display1',
                        }}
                        titleProps={{
                          textWeight: '500',
                          textSize: 'body',
                          textColor: 'marigold',
                        }}
                      />
                    </Row>
                  </Col>
                  <Col size="10" d="flex" align="stretch" justify="stretch" gutterWidth="0">
                    <Row w="100%" h="100%" m={{ l: '.5rem' }}>
                      <Col gutterWidth="0" border="1px solid" borderColor="shadeD9" rounded="md" size="12">
                        <Div p={{ t: '.5rem', r: '.5rem' }} m={{ b: '-2rem' }} h="100%">
                          {entries && entries.length > 0 && (
                            <charts.VerticalBar
                              {...chartData}
                              formatter={(params) => formatNumber(params.value)}
                              options={chartOptions}
                              theme={stackedTheme}
                              onThemeChange={setStackedTheme}
                              usePalette
                              showPalette={false}
                              markLine={false}
                            />
                          )}
                        </Div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Message
                value={null}
                bg="white"
                type="info"
                text="No data"
                cta={{ text: 'Refresh', onClick: loadRtData }}
              />
            </Switch>
          </Card>
        </Col>
      </Row>
    </Div>
  );
}

const mapStateToProps = ({ app, rtApiDemo, rtApiList, rtApiUsage }) => ({ app, rtApiUsage });

const mapDispatchToProps = (dispatch) => ({
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  fetchConfig: (configId) => dispatch(rtApiUsageActions.fetchConfig(configId)),
  fetchConfigList: (query) => dispatch(rtApiUsageActions.fetchConfigList(query)),
  handleUnmount: () => {
    dispatch(rtApiUsageActions.unset('config'));
    dispatch(rtApiUsageActions.unset('configList'));
  },
  fetchEntries: (query) => dispatch(rtApiUsageActions.fetchEntries(query)),
  generateStats: (...params) => dispatch(rtApiUsageActions.generateStats(...params)),
});

RtApiUsage.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    }),
    client: PropTypes.shape({
      billingDay: PropTypes.number.isRequired,
    }),
  }).isRequired,
  rtApiUsage: PropTypes.shape({
    config: PropTypes.shape({
      id: PropTypes.string.isRequired,
      config_metadata: PropTypes.shape({
        model: PropTypes.shape({
          name: PropTypes.string.isRequired,
        }),
      }).isRequired,
    }),
    fetchConfigStatus: PropTypes.string.isRequired,
    fetchConfigError: PropTypes.string.isRequired,
    configList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fetchConfigListStatus: PropTypes.string.isRequired,
    fetchConfigListError: PropTypes.string.isRequired,
    entries: PropTypes.arrayOf(
      PropTypes.shape({
        period: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        meta: PropTypes.string,
      })
    ),
    masks: PropTypes.shape({
      ytdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      mtdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      mtdTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      tdyMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      tdyTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      btdUnmatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    }).isRequired,
    fetchEntriesStatus: PropTypes.string.isRequired,
    fetchEntriesError: PropTypes.string.isRequired,
    ytdCount: PropTypes.number,
    mtdCount: PropTypes.number,
    tdyCount: PropTypes.number,
    generateStatsStatus: PropTypes.string.isRequired,
    generateStatsError: PropTypes.string.isRequired,
  }).isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  fetchConfig: PropTypes.func.isRequired,
  fetchConfigList: PropTypes.func.isRequired,
  fetchEntries: PropTypes.func.isRequired,
  generateStats: PropTypes.func.isRequired,
  handleUnmount: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(RtApiUsage));
